import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import './SetupAgentPage.css';
import './ViewAgentPage.css';
import { 
  getAvailableEnvironments, 
  getSelectedEnvironment, 
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';

const AgentActivityReportPage = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());
  const availableEnvironments = getAvailableEnvironments();
  
  // Get API endpoints based on selected environment
  const apiEndpoints = getApiEndpoints(selectedEnvironment);
  const INSTANCE_DROPDOWN_API = apiEndpoints.instanceDropdown;
  const AGENT_ACTIVITY_API = apiEndpoints.agentActivity;

  // Single agent form state
  const [agentActivityForm, setAgentActivityForm] = useState({
      instance_id: '',
  });

  // Handle environment change
  const handleEnvironmentChange = (e) => {
    const newEnvironment = e.target.value;
      setSelectedEnvironment(newEnvironment);
      saveSelectedEnvironment(newEnvironment);
      setAgents([]);
      setError(null);
  };

  const handleAgentActivityFormChange = (e) => {
    const { name, value } = e.target;
    
    setAgentActivityForm(prev => ({ ...prev, [name]: value }));
    if (name === 'instance_id') {
      setAgents([]);
      setError(null);
    }
  };


  const handleAgentActivitySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await fetchAgentActivityList(agentActivityForm.instance_id);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to acquire list of agents');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentActivityList = async (instanceId) => {
    if (!instanceId) {
      console.warn('[Agent Activity] Request skipped: instance_id is empty');
      setAgents([]);
      return;
    }

    setLoadingAgents(true);
    const payload = {
      instance_id: instanceId,
      include_zero_rows: true,
    };
    console.info('[Agent Activity] Request:', {
      endpoint: AGENT_ACTIVITY_API,
      payload,
    });

    try {
      const response = await apiService.post(AGENT_ACTIVITY_API, payload);
      console.info('[Agent Activity] Response:', {
        httpStatus: response.status,
        data: response.data,
      });

      if (response.data.status === 'ok') {
        const rows = response.data.result?.rows || [];
        console.info('[Agent Activity] Rows received:', rows.length, rows);
        setAgents(rows);
        return;
      }

      const message = response.data.message || response.data.result?.message || 'Unknown API error';
      console.error('[Agent Activity] API returned an error:', {
        message,
        data: response.data,
      });
      throw new Error(message);
    } catch (err) {
      console.error('[Agent Activity] Request failed:', {
        message: err.message,
        httpStatus: err.response?.status,
        responseData: err.response?.data,
        error: err,
      });
      throw err;
    } finally {
      setLoadingAgents(false);
    }
  };

  const formatSeconds = (value) => {
    if (value === null || value === undefined) return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return '-';
    return num.toLocaleString();
  };

  const getSortValue = (agent, key) => {
      switch (key) {
        case 'agent_name':
          return (agent.agent_name || '').toLowerCase();
        case 'username':
          return (agent.username || '').toLowerCase();
        case 'number_contacts_handled':
          return Number(agent.number_contacts_handled || 0);
        case 'talk_time':
          return Number(agent.talk_time || 0);
        case 'hold_time':
          return Number(agent.hold_time || 0);
        case 'after_contact_work':
          return Number(agent.after_contact_work || 0);
        case 'average_handle_time':
          return Number(agent.average_handle_time || 0);
        case 'available_time':
          return Number(agent.available_time || 0);
        default:
          return '';
      }
    };
  
    const handleSort = (key) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
      setPage(1);
    };
  
    const sortIndicator = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  
    const filteredAgents = React.useMemo(() => {
      const term = searchTerm.trim().toLowerCase();
      if (!term) return agents;
      return agents.filter((agent) => {
        const name = (agent.agent_name || '').toLowerCase();
        const username = (agent.username || '').toLowerCase();
        return name.includes(term) || username.includes(term);
      });
    }, [agents, searchTerm]);
  
    const sortedAgents = React.useMemo(() => {
      if (!sortKey) return filteredAgents;
      const copy = [...filteredAgents];
      copy.sort((a, b) => {
        const va = getSortValue(a, sortKey);
        const vb = getSortValue(b, sortKey);
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
      return copy;
    }, [filteredAgents, sortKey, sortDir]);
  
  const totalPages = Math.max(1, Math.ceil(sortedAgents.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedAgents = sortedAgents.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };
  
  // Fetch Connect instances on component mount and when environment changes
  useEffect(() => {
    const fetchInstances = async () => {
      setLoadingInstances(true);
      try {
        const response = await apiService.get(INSTANCE_DROPDOWN_API);
        if (response.data.status === 'ok') {
          setInstances(response.data.instances || []);
        } else {
          console.error('Failed to load instances:', response.data.message);
        }
      } catch (err) {
        console.error('Error fetching instances:', err);
      } finally {
        setLoadingInstances(false);
      }
    };

    fetchInstances();
  }, [INSTANCE_DROPDOWN_API, selectedEnvironment]);

  return (
      <div className="setup-page">
        <div className="page-header">
         <div>
            <h1>Agent Activity Report</h1>
              <p className="page-subtitle">View Agent Activity</p>
          </div>
          <Link to="/reports" className="btn btn-secondary">
             ← Back to Reports
          </Link>
        </div>
  
        {/* Environment Selector */}
        <div className="environment-selector" style={{
          backgroundColor: '#f9f9f9',
          border: '2px solid #e0e0e0',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
        <label htmlFor="environment" style={{ 
          fontWeight: 'bold', 
          fontSize: '16px',
          minWidth: '120px'
        }}>
          Environment:
        </label>
        <select
          id="environment"
          value={selectedEnvironment}
          onChange={handleEnvironmentChange}
          style={{
            padding: '10px 16px',
            fontSize: '15px',
            borderRadius: '6px',
            border: '2px solid #ccc',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontWeight: '500',
            minWidth: '200px'
          }}
         >
         {availableEnvironments.map(env => (
            <option key={env} value={env}>
              {ENVIRONMENT_LABELS[env]} ({env})
            </option>
          ))}
          </select>
          <span style={{
            padding: '6px 12px',
            borderRadius: '4px',
            fontSize: '13px',
            fontWeight: 'bold',
            color: 'white',
            backgroundColor: getEnvironmentColor(selectedEnvironment)
          }}>
            {selectedEnvironment}
          </span>
        </div>

       <div className="form-container">
            <form onSubmit={handleAgentActivitySubmit} className="setup-form">
                <div className="form-section">
                <h3>Instance Information</h3>
                    <div className="form-group">
                        <label htmlFor="instance_id">Instance *</label>
                        <select
                        id="instance_id"
                        name="instance_id"
                        value={agentActivityForm.instance_id}
                        onChange={handleAgentActivityFormChange}
                        required
                        disabled={loadingInstances}
                        >
                        <option value="">
                            {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                        </option>
                        {instances.map((instance) => (
                            <option key={instance.id} value={instance.id}>
                            {instance.name} ({instance.status})
                            </option>
                        ))}
                        </select>
                        <small>Select an Amazon Connect instance</small>
                    </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading || loadingAgents || !agentActivityForm.instance_id}>
                    {loading || loadingAgents ? 'Viewing...' : 'View Activity'}
                  </button>
              </div>
            </form>
        </div>  

        {(loading || loadingAgents) && (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Processing your request...</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
          
        {agents.length > 0 && (
          <div className="table-format-list">
            <div className="table-format-list-header">
              <h3>Agents ({sortedAgents.length}{sortedAgents.length !== agents.length ? ` of ${agents.length}` : ''})</h3>
              <div className="table-format-list-controls">
                <input
                  type="text"
                  className="table-format-search-input"
                  placeholder="Search agent name or username..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                />
                <div className="page-size-selector">
                  <label htmlFor="page_size">Rows per page:</label>
                  <select id="page_size" value={pageSize} onChange={handlePageSizeChange}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('agent_name')}>Agent Name{sortIndicator('agent_name')}</th>
                  <th className="sortable" onClick={() => handleSort('username')}>Username{sortIndicator('username')}</th>
                  <th className="sortable" onClick={() => handleSort('number_contacts_handled')}>Contacts Handled{sortIndicator('number_contacts_handled')}</th>
                  <th className="sortable" onClick={() => handleSort('talk_time')}>Talk Time{sortIndicator('talk_time')}</th>
                  <th className="sortable" onClick={() => handleSort('hold_time')}>Hold Time{sortIndicator('hold_time')}</th>
                  <th className="sortable" onClick={() => handleSort('after_contact_work')}>After Call Work{sortIndicator('after_contact_work')}</th>
                  <th className="sortable" onClick={() => handleSort('average_handle_time')}>Average Handle Time{sortIndicator('average_handle_time')}</th>
                  <th className="sortable" onClick={() => handleSort('available_time')}>Available Time{sortIndicator('available_time')}</th>
                </tr>
              </thead>
              <tbody>
                {pagedAgents.map((agent) => (
                  <tr key={agent.username || agent.agent_name}>
                    <td>{agent.agent_name || '-'}</td>
                    <td>{agent.username || '-'}</td>
                    <td>{agent.number_contacts_handled ?? '-'}</td>
                    <td>{formatSeconds(agent.talk_time)}</td>
                    <td>{formatSeconds(agent.hold_time)}</td>
                    <td>{formatSeconds(agent.after_contact_work)}</td>
                    <td>{formatSeconds(agent.average_handle_time)}</td>
                    <td>{formatSeconds(agent.available_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination-controls">
              <button type="button" className="btn btn-secondary" onClick={() => setPage(1)} disabled={currentPage === 1}>« First</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹ Prev</button>
              <span className="pagination-status">Page {currentPage} of {totalPages}</span>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ›</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}>Last »</button>
            </div>
          </div>
        )}
        {!loading && agents.length === 0 && (
          <div className="agent-list">
            <h3>Agents ({agents.length})</h3>
            <p>No agents found.</p>
          </div>
        )}

    </div>
  );
}

export default AgentActivityReportPage;